import { derived, get, writable } from "svelte/store";
import { filtersToApiParams, formatCoordinates, parseCoordinates } from "../components/station/apiUtils";
import * as SpatialIndex from "../components/station/SpatialIndex.js";
const BASE_URL = new URL(process.env.SEARCH_URL);
const STATIONS_URL = `${BASE_URL}/v2/stations/simple`;
const CONFIG = {
    DEBOUNCE_DELAY: 800,
    DEFAULT_LIMIT: 1000,
    STATION_CACHE_LIMIT: 25000,
    MAX_CONCURRENT_REQUESTS: 2,
    MIN_NAME_SEARCH_LENGTH: 2
};
// API
function transformStation(apiStation, index) {
    var _a, _b, _c;
    const coords = apiStation.location || apiStation.geometry || [];
    const [longitude, latitude] = coords;
    if (typeof longitude !== "number" || typeof latitude !== "number")
        return null;
    const identifiers = Array.isArray(apiStation.identifiers)
        ? apiStation.identifiers.map((id) => ({ id: id.id, idType: id.network }))
        : [];
    const uniqueId = apiStation.id ||
        ((_a = identifiers.find((id) => id.idType === "NCDCSTNID")) === null || _a === void 0 ? void 0 : _a.id) ||
        ((_b = identifiers[0]) === null || _b === void 0 ? void 0 : _b.id) ||
        `STATION_${index}`;
    return {
        id: uniqueId,
        identifiers,
        name: apiStation.preferredName || "Unknown Station",
        otherNames: apiStation.otherNames,
        latitude,
        longitude,
        beginDate: (apiStation.startDate && apiStation.startDate !== "0001-01-01") ? apiStation.startDate : "Unknown",
        endDate: (apiStation.endDate && apiStation.endDate !== "9999-12-31") ? apiStation.endDate : "Present",
        prefix: ((_c = identifiers[0]) === null || _c === void 0 ? void 0 : _c.idType) || "",
        datasets: apiStation.datasets || [],
    };
}
function buildUrl(endpoint, params = {}) {
    const url = new URL(endpoint);
    Object.entries(params).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== "") {
            url.searchParams.append(key, String(value));
        }
    });
    return url.toString();
}
export async function fetchStations(filters = {}, options = {}) {
    const params = filtersToApiParams(filters);
    const url = buildUrl(STATIONS_URL, params);
    const response = await fetch(url, { signal: options.signal });
    if (!response.ok)
        throw new Error(`API returned ${response.status}`);
    const data = await response.json();
    const results = Array.isArray(data) ? data : (data.results || []);
    const stations = results.map(transformStation).filter(Boolean);
    return {
        stations,
        total: data.count || results.length,
        availableNetworks: data["available-networks"] || [],
        availableDatasets: data["available-datasets"] || [],
        startDate: data["startDate"] || null,
        endDate: data["endDate"] || null,
        metadata: {
            offset: params.offset || 0,
            limit: params.limit || results.length,
            hasMore: data.count ? data.count > (params.offset || 0) + stations.length : false
        }
    };
}
export async function fetchStation(id) {
    const url = buildUrl(STATIONS_URL, { id, limit: 1 });
    const response = await fetch(url);
    if (!response.ok)
        throw new Error(`API returned ${response.status}`);
    const data = await response.json();
    const results = Array.isArray(data) ? data : (data.results || []);
    if (!results.length)
        return null;
    return transformStation(results[0], 0);
}
// REQUEST MANAGEMENT
let activeRequestCount = 0;
const activeRequests = new Map();
let searchTimeout = null;
let coordinateSearchTimeout = null;
function canMakeRequest() {
    return activeRequestCount < CONFIG.MAX_CONCURRENT_REQUESTS;
}
function cancelActiveRequests() {
    for (const [, { controller }] of activeRequests)
        controller.abort();
    activeRequests.clear();
    activeRequestCount = 0;
}
// STORES
export const stationsSummary = writable();
export const resultSummary = writable();
export const allStationsMap = writable(new Map());
export const allStations = derived(allStationsMap, $map => Array.from($map.values()));
export const selectedStations = writable([]);
export const highlightedStation = writable(null);
export const focusedStation = writable(null);
export const isLoading = writable(true);
export const filters = writable({
    name: "",
    id: "",
    location: "",
    dateStart: "",
    dateEnd: "",
    datasetId: "",
    networkId: "",
    viewport: null,
    customDateStart: null,
    customDateEnd: null,
    offset: 0,
    limit: CONFIG.DEFAULT_LIMIT,
});
export const viewMode = writable("viewport");
export const spatialFilter = writable(null);
// What the coordinate boxes show (separate from filters.location, the search value).
export const coordinateInput = writable({ lat: "", lng: "" });
export const mapInstance = writable(null);
export const drawInstance = writable(null);
export const error = writable(null);
export const currentViewportBounds = writable(null);
export const viewportSystem = writable(null);
export const drawingSystem = writable(null);
// New object each time so a repeat request still fires.
export const cameraRequest = writable(null);
function requestCamera(kind) {
    cameraRequest.set({ kind });
}
export const mapDisplayStations = derived([allStations, spatialFilter], ([$allStations, $spatialFilter]) => {
    var _a;
    if ((_a = $spatialFilter === null || $spatialFilter === void 0 ? void 0 : $spatialFilter.filteredStations) === null || _a === void 0 ? void 0 : _a.length) {
        const ids = new Set($spatialFilter.filteredStations);
        return $allStations.filter(s => ids.has(s.id));
    }
    return $allStations;
});
export const displayStations = derived([allStations, spatialFilter, viewMode, currentViewportBounds, selectedStations], ([$allStations, $spatialFilter, $viewMode, $bounds, $selectedStations]) => {
    var _a;
    let stations;
    if ((_a = $spatialFilter === null || $spatialFilter === void 0 ? void 0 : $spatialFilter.filteredStations) === null || _a === void 0 ? void 0 : _a.length) {
        const ids = new Set($spatialFilter.filteredStations);
        stations = $allStations.filter(s => ids.has(s.id));
    }
    else if ($viewMode === "viewport" && $bounds) {
        const selectedSet = new Set($selectedStations);
        stations = $allStations.filter(s => selectedSet.has(s.id) ||
            (s.longitude >= $bounds.west && s.longitude <= $bounds.east &&
                s.latitude >= $bounds.south && s.latitude <= $bounds.north));
    }
    else {
        stations = $allStations;
    }
    if (!$selectedStations.length)
        return stations;
    const selectedSet = new Set($selectedStations);
    return [
        ...stations.filter(s => selectedSet.has(s.id)),
        ...stations.filter(s => !selectedSet.has(s.id))
    ];
});
export const totalStationsCount = derived(allStations, $all => $all.length);
export const hasActiveFilters = derived(filters, $f => !!($f.name || $f.id || $f.location || $f.datasetId || $f.networkId || $f.customDateStart || $f.customDateEnd));
export const isSpatiallyFiltered = derived(spatialFilter, $sf => { var _a; return !!((_a = $sf === null || $sf === void 0 ? void 0 : $sf.filteredStations) === null || _a === void 0 ? void 0 : _a.length); });
// ACTIONS
export const loadingActions = {
    start: () => isLoading.set(true),
    stop: () => isLoading.set(false),
};
export const selectionActions = {
    toggle(stationId) {
        selectedStations.update(stations => {
            const newStations = stations.includes(stationId)
                ? stations.filter(id => id !== stationId)
                : [...stations, stationId];
            updateUrl(newStations);
            return newStations;
        });
    },
    add(stationIds) {
        const ids = Array.isArray(stationIds) ? stationIds : [stationIds];
        if (!ids.length)
            return;
        selectedStations.update(stations => {
            const set = new Set(stations);
            ids.forEach(id => id && set.add(id));
            const newStations = Array.from(set);
            updateUrl(newStations);
            return newStations;
        });
    },
    remove(stationIds) {
        const ids = new Set(Array.isArray(stationIds) ? stationIds : [stationIds]);
        selectedStations.update(stations => {
            const newStations = stations.filter(id => !ids.has(id));
            updateUrl(newStations);
            return newStations;
        });
    },
    clear() {
        selectedStations.set([]);
        highlightedStation.set(null);
        updateUrl([]);
    },
};
export const stationDataActions = {
    async loadStations(filterOverrides = {}) {
        if (!canMakeRequest()) {
            return { stations: [], total: 0, availableNetworks: [], availableDatasets: [], startDate: null, endDate: null, metadata: { offset: 0, limit: 0, hasMore: false } };
        }
        const currentFilters = get(filters);
        const mergedFilters = Object.assign(Object.assign(Object.assign({}, currentFilters), filterOverrides), { limit: Math.min(filterOverrides.limit || currentFilters.limit || CONFIG.DEFAULT_LIMIT, 1000) });
        const requestKey = JSON.stringify(mergedFilters);
        const existing = activeRequests.get(requestKey);
        if (existing) {
            try {
                return await existing.promise;
            }
            catch (_a) {
                return { stations: [], total: 0, availableNetworks: [], availableDatasets: [], startDate: null, endDate: null, metadata: { offset: 0, limit: 0, hasMore: false } };
            }
        }
        const hasSearchFilters = !!(mergedFilters.name || mergedFilters.id || mergedFilters.location ||
            mergedFilters.customDateStart || mergedFilters.customDateEnd ||
            mergedFilters.bbox || mergedFilters.radius || mergedFilters.datasetId || mergedFilters.networkId);
        viewMode.set(hasSearchFilters ? "search" : "viewport");
        loadingActions.start();
        error.set(null);
        activeRequestCount++;
        const controller = new AbortController();
        const fetchPromise = fetchStations(mergedFilters, { signal: controller.signal })
            .then(result => {
            if (!(result === null || result === void 0 ? void 0 : result.stations))
                throw new Error('Invalid response');
            resultSummary.set({
                networks: result === null || result === void 0 ? void 0 : result.availableNetworks,
                datasets: result === null || result === void 0 ? void 0 : result.availableDatasets,
                startDate: result === null || result === void 0 ? void 0 : result.startDate,
                endDate: result === null || result === void 0 ? void 0 : result.endDate
            });
            const resultsMap = new Map();
            result.stations.forEach(s => (s === null || s === void 0 ? void 0 : s.id) && resultsMap.set(s.id, s));
            const currentMode = get(viewMode);
            const shouldUpdate = (hasSearchFilters && currentMode === "search") || (!hasSearchFilters && currentMode === "viewport");
            if (shouldUpdate) {
                const isSpatialQuery = !!(mergedFilters.bbox || mergedFilters.radius);
                if (isSpatialQuery || currentMode === "viewport") {
                    allStationsMap.update(currentMap => {
                        const updated = new Map(currentMap);
                        resultsMap.forEach((station, id) => updated.set(id, station));
                        if (updated.size > CONFIG.STATION_CACHE_LIMIT) {
                            return new Map(Array.from(updated.entries()).slice(-CONFIG.STATION_CACHE_LIMIT));
                        }
                        return updated;
                    });
                }
                else {
                    // Search replaced the whole set, so the camera should follow it.
                    allStationsMap.set(resultsMap);
                    if (result.stations.length)
                        requestCamera("fitResults");
                }
                SpatialIndex.updateIndex(result.stations);
            }
            return result;
        });
        activeRequests.set(requestKey, { promise: fetchPromise, controller });
        try {
            return await fetchPromise;
        }
        catch (err) {
            if (!(err instanceof Error && err.name === 'AbortError')) {
                const msg = err instanceof Error ? err.message : "Unknown error";
                error.set(msg);
                console.error('Station load failed:', msg);
            }
            throw err;
        }
        finally {
            activeRequestCount = Math.max(0, activeRequestCount - 1);
            activeRequests.delete(requestKey);
            loadingActions.stop();
        }
    },
    mergeStations(newStations) {
        if (!(newStations === null || newStations === void 0 ? void 0 : newStations.length))
            return;
        allStationsMap.update(currentMap => {
            const updated = new Map(currentMap);
            newStations.forEach(s => (s === null || s === void 0 ? void 0 : s.id) && updated.set(s.id, s));
            if (updated.size > CONFIG.STATION_CACHE_LIMIT) {
                const limited = new Map(Array.from(updated.entries()).slice(-CONFIG.STATION_CACHE_LIMIT));
                SpatialIndex.updateIndex(Array.from(limited.values()));
                return limited;
            }
            SpatialIndex.updateIndex(newStations);
            return updated;
        });
    },
    updateStations(stations) {
        if (!Array.isArray(stations))
            return;
        const map = new Map();
        stations.forEach(s => (s === null || s === void 0 ? void 0 : s.id) && map.set(s.id, s));
        allStationsMap.set(map);
    },
    clearStations() {
        allStationsMap.set(new Map());
        SpatialIndex.clear();
    },
};
export const filterActions = {
    updateFilters(updates) {
        if (!updates || typeof updates !== 'object')
            return;
        filters.update(f => (Object.assign(Object.assign({}, f), updates)));
        if (updates.datasetId !== undefined || updates.networkId !== undefined) {
            if (searchTimeout) {
                clearTimeout(searchTimeout);
                searchTimeout = null;
            }
            stationDataActions.loadStations(get(filters));
            return;
        }
        if (updates.name !== undefined || updates.id !== undefined || updates.location !== undefined) {
            if (searchTimeout) {
                clearTimeout(searchTimeout);
                searchTimeout = null;
            }
            searchTimeout = setTimeout(() => {
                var _a, _b, _c;
                const f = get(filters);
                const hasValidSearch = !!((f.name && f.name.length >= CONFIG.MIN_NAME_SEARCH_LENGTH) ||
                    f.id || f.location || f.customDateStart || f.customDateEnd);
                if (!hasValidSearch) {
                    spatialFilter.set(null);
                    stationDataActions.clearStations();
                    (_b = (_a = get(viewportSystem)) === null || _a === void 0 ? void 0 : _a.clearCache) === null || _b === void 0 ? void 0 : _b.call(_a);
                    viewMode.set("viewport");
                    return;
                }
                stationDataActions.loadStations(Object.assign(Object.assign({}, f), { viewport: null, bbox: null, radius: null, limit: 1000, name: (_c = f.name) === null || _c === void 0 ? void 0 : _c.toUpperCase() }));
            }, CONFIG.DEBOUNCE_DELAY);
        }
    },
    setSpatialFilter(filterData) {
        if (filterData && !Array.isArray(filterData.filteredStations))
            return;
        spatialFilter.set(filterData);
    },
    clearSpatialFilter() {
        spatialFilter.set(null);
    },
    // Show coordinates in the boxes without searching (used by drawn shapes).
    setCoordinateDisplay(lat, lng) {
        if (coordinateSearchTimeout) {
            clearTimeout(coordinateSearchTimeout);
            coordinateSearchTimeout = null;
        }
        coordinateInput.set({ lat: lat !== null && lat !== void 0 ? lat : "", lng: lng !== null && lng !== void 0 ? lng : "" });
    },
    clearCoordinateDisplay() {
        if (coordinateSearchTimeout) {
            clearTimeout(coordinateSearchTimeout);
            coordinateSearchTimeout = null;
        }
        coordinateInput.set({ lat: "", lng: "" });
    },
    // Clear the search value before a shape fetch so it can't hijack the shape's bbox/radius.
    clearLocationFilter() {
        if (coordinateSearchTimeout) {
            clearTimeout(coordinateSearchTimeout);
            coordinateSearchTimeout = null;
        }
        filters.update(f => (Object.assign(Object.assign({}, f), { location: "" })));
    },
    // Debounced coordinate search; a valid coordinate supersedes any drawn shape.
    searchByCoordinates(lat, lng) {
        coordinateInput.set({ lat: lat !== null && lat !== void 0 ? lat : "", lng: lng !== null && lng !== void 0 ? lng : "" });
        if (coordinateSearchTimeout) {
            clearTimeout(coordinateSearchTimeout);
            coordinateSearchTimeout = null;
        }
        const location = formatCoordinates(lat, lng);
        const bothEmpty = !String(lat !== null && lat !== void 0 ? lat : "").trim() && !String(lng !== null && lng !== void 0 ? lng : "").trim();
        coordinateSearchTimeout = setTimeout(() => {
            var _a, _b, _c, _d, _e, _f;
            if (location && parseCoordinates(location)) {
                filters.update(f => (Object.assign(Object.assign({}, f), { location })));
                (_b = (_a = get(drawingSystem)) === null || _a === void 0 ? void 0 : _a.clearShape) === null || _b === void 0 ? void 0 : _b.call(_a);
                stationDataActions.loadStations(Object.assign(Object.assign({}, get(filters)), { viewport: null, bbox: null, radius: null, limit: 1000 }));
                return;
            }
            filters.update(f => (Object.assign(Object.assign({}, f), { location: "" })));
            // Empty boxes reset to viewport, but a drawn shape stays the selection.
            if (bothEmpty && !((_d = (_c = get(drawingSystem)) === null || _c === void 0 ? void 0 : _c.hasActiveShape) === null || _d === void 0 ? void 0 : _d.call(_c))) {
                spatialFilter.set(null);
                stationDataActions.clearStations();
                (_f = (_e = get(viewportSystem)) === null || _e === void 0 ? void 0 : _e.clearCache) === null || _f === void 0 ? void 0 : _f.call(_e);
                viewMode.set("viewport");
            }
        }, CONFIG.DEBOUNCE_DELAY);
    },
    resetFilters() {
        var _a, _b;
        if (searchTimeout) {
            clearTimeout(searchTimeout);
            searchTimeout = null;
        }
        if (coordinateSearchTimeout) {
            clearTimeout(coordinateSearchTimeout);
            coordinateSearchTimeout = null;
        }
        // Free the slots or the next search gets dropped by the concurrency guard.
        cancelActiveRequests();
        const viewport = get(filters).viewport;
        filters.set({
            name: "", id: "", location: "", dateStart: "", dateEnd: "",
            datasetId: "", networkId: "", viewport,
            customDateStart: null, customDateEnd: null,
            offset: 0, limit: CONFIG.DEFAULT_LIMIT,
        });
        spatialFilter.set(null);
        coordinateInput.set({ lat: "", lng: "" });
        highlightedStation.set(null);
        stationDataActions.clearStations();
        // A stale bounds key here makes the refetch below skip.
        (_b = (_a = get(viewportSystem)) === null || _a === void 0 ? void 0 : _a.clearCache) === null || _b === void 0 ? void 0 : _b.call(_a);
        viewMode.set("viewport");
        error.set(null);
        requestCamera("home");
        stationDataActions.loadStations();
    },
    setDateRange(start, end) {
        if (start && isNaN(Date.parse(start)))
            return;
        if (end && isNaN(Date.parse(end)))
            return;
        filters.update(f => (Object.assign(Object.assign({}, f), { customDateStart: start, customDateEnd: end })));
        stationDataActions.loadStations(Object.assign(Object.assign({}, get(filters)), { limit: 1000 }));
    },
    cleanup() {
        if (searchTimeout) {
            clearTimeout(searchTimeout);
            searchTimeout = null;
        }
        if (coordinateSearchTimeout) {
            clearTimeout(coordinateSearchTimeout);
            coordinateSearchTimeout = null;
        }
        cancelActiveRequests();
    },
};
// URL
function updateUrl(stations) {
    if (typeof window === "undefined")
        return;
    const url = new URL(window.location.href);
    stations.length ? url.searchParams.set("stations", stations.join(",")) : url.searchParams.delete("stations");
    window.history.replaceState({}, "", url.toString());
}
export async function initFromUrl() {
    if (typeof window !== "undefined") {
        const params = new URLSearchParams(window.location.search);
        const stationsParam = params.get("stations");
        if (stationsParam) {
            const rawIds = stationsParam.split(",").map(id => id.trim()).filter(Boolean);
            const resolvedStations = (await Promise.all(rawIds.map(fetchStation))).filter(Boolean);
            if (resolvedStations.length) {
                stationDataActions.mergeStations(resolvedStations);
                selectedStations.set(resolvedStations.map(s => s.id));
            }
        }
    }
    try {
        const resp = await fetch(`${BASE_URL}/v2/stations/summary`);
        if (!resp.ok)
            throw new Error(`API returned ${resp.status}`);
        const summary = await resp.json();
        stationsSummary.set({
            datasets: summary["available-datasets"] || [],
            networks: summary["available-networks"] || [],
            startDate: summary["startDate"],
            endDate: summary["endDate"]
        });
    }
    catch (err) {
        console.error("Failed to load station summary:", err);
    }
}
if (typeof window !== "undefined") {
    window.addEventListener('beforeunload', () => filterActions.cleanup());
}
